'use client';

import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { toast } from 'sonner';

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      if (mutation.meta?.success) {
        toast.success(mutation.meta?.success?.title, {
          description: mutation.meta?.success?.message,
        });
      }
    },
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.error) {
        toast.error(mutation.meta.error.title, {
          description: mutation.meta.error.message(error),
        });
      }
    },
    onSettled: (data, error, _variables, _context, mutation) => {
      if (mutation.meta?.invalidateQuery) {
        queryClient.invalidateQueries({
          queryKey: mutation.meta?.invalidateQuery,
          exact: false,
        });
      }
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      retry: 1,
      staleTime: 1000 * 30,
      gcTime: 1000 * 60 * 5,
      // No default refetchInterval: polling every query on a 30s timer regardless of
      // whether it needs live data was silently overriding hooks that set a longer
      // staleTime specifically to reduce load (e.g. dashboard/report screens).
      // Screens that genuinely need live polling set their own refetchInterval.
    },
  },
});

export function QueryProvider({ children }: { children: React.ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
